import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useOnboarding } from '../components/OnboardingTour';
import {
  getBackupSummary,
  exportDataJson,
  exportDataZip,
  getNotificationPreferences,
  updateNotificationPreferences,
  getUpcomingDeadlines,
  getSyncHistory,
  triggerAutoSync,
  getPlaidStatus,
  deleteAccount,
  getEmailPreferences,
  updateEmailPreferences,
  getShoeboxEmail,
  sendTestEmail,
  getUserDataAccess,
  toggleMergeData
} from '../lib/api';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Download,
  Bell,
  Calendar,
  Database,
  FileJson,
  FileArchive,
  Loader2,
  Save,
  AlertTriangle,
  Clock,
  Mail,
  Receipt,
  DollarSign,
  Car,
  Smartphone,
  BellRing,
  BellOff,
  Send,
  RefreshCw,
  History,
  CheckCircle2,
  XCircle,
  Trash2,
  HelpCircle,
  Briefcase,
  Home,
  Merge,
  GitMerge
} from 'lucide-react';

const daysOfWeek = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' }
];

const timeOptions = [
  { value: '08:00', label: '8:00 AM' },
  { value: '09:00', label: '9:00 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '14:00', label: '2:00 PM' },
  { value: '16:00', label: '4:00 PM' },
  { value: '18:00', label: '6:00 PM' },
  { value: '20:00', label: '8:00 PM' }
];

export default function Settings() {
  const { user } = useAuth();
  const { startTour } = useOnboarding() || {};
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportType, setExportType] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  
  // Backup summary
  const [backupSummary, setBackupSummary] = useState(null);
  
  // Notification preferences
  const [prefs, setPrefs] = useState(null);
  
  // Email preferences
  const [shoeboxEmail, setShoeboxEmail] = useState('');
  const [emailPrefs, setEmailPrefs] = useState({
    weekly_summary: true,
    tax_reminders: true,
    receipt_notifications: true,
    subscription_updates: true
  });
  
  // Upcoming deadlines
  const [deadlines, setDeadlines] = useState([]);
  
  // Sync history & status
  const [syncHistory, setSyncHistory] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [plaidStatus, setPlaidStatus] = useState({ connected: false, items_count: 0, last_sync: null });
  
  // Delete account confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Data access & merge settings
  const [dataAccess, setDataAccess] = useState({
    has_business_access: true,
    has_personal_access: true,
    can_merge_data: true,
    merge_enabled: false
  });
  const [togglingMerge, setTogglingMerge] = useState(false);
  
  // Push notifications
  const {
    isSupported: pushSupported,
    isSubscribed: pushSubscribed,
    permission: pushPermission,
    loading: pushLoading,
    subscribe: subscribeToPush,
    unsubscribe: unsubscribeFromPush,
    sendTestNotification
  } = usePushNotifications();

  async function fetchData() {
    try {
      setLoading(true);
      const [summaryRes, prefsRes, deadlinesRes, shoeboxRes, emailPrefsRes, dataAccessRes] = await Promise.all([
        getBackupSummary(),
        getNotificationPreferences(),
        getUpcomingDeadlines(),
        getShoeboxEmail(),
        getEmailPreferences(),
        getUserDataAccess()
      ]);
      setBackupSummary(summaryRes.data);
      setPrefs(prefsRes.data);
      setDeadlines(deadlinesRes.data);
      setShoeboxEmail(shoeboxRes.data?.email || '');
      setEmailPrefs(emailPrefsRes.data || {
        weekly_summary: true,
        tax_reminders: true,
        receipt_notifications: true,
        subscription_updates: true
      });
      setDataAccess(dataAccessRes.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }
  
  async function handleToggleMerge() {
    try {
      setTogglingMerge(true);
      const newValue = !dataAccess.merge_enabled;
      await toggleMergeData(newValue);
      setDataAccess(prev => ({ ...prev, merge_enabled: newValue }));
      toast.success(newValue ? 'Data merge enabled' : 'Data merge disabled');
    } catch (error) {
      toast.error('Failed to update merge settings');
    } finally {
      setTogglingMerge(false);
    }
  }

  useEffect(function() {
    fetchData();
  }, []);

  async function handleTogglePush() {
    try {
      if (pushSubscribed) {
        await unsubscribeFromPush();
        toast.success('Push notifications disabled');
      } else {
        await subscribeToPush();
        toast.success('Push notifications enabled!');
      }
    } catch (error) {
      if (error.message.includes('denied')) {
        toast.error('Please allow notifications in your browser settings');
      } else {
        toast.error('Failed to update push notifications');
      }
    }
  }

  async function handleSendTestPush() {
    setSendingTest(true);
    try {
      const result = await sendTestNotification();
      if (result.success > 0) {
        toast.success('Test notification sent!');
      } else {
        toast.error('No active subscriptions found');
      }
    } catch (error) {
      toast.error('Failed to send test notification');
    } finally {
      setSendingTest(false);
    }
  }

  async function handleExportJson() {
    setExporting(true);
    setExportType('json');
    try {
      const res = await exportDataJson();
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'zero_deficit_flow_backup.json';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Data exported as JSON');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
      setExportType(null);
    }
  }

  async function handleExportZip() {
    setExporting(true);
    setExportType('zip');
    try {
      const res = await exportDataZip();
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'zero_deficit_flow_backup.zip';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Data exported as ZIP with files');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
      setExportType(null);
    }
  }

  async function handleDeleteAccount() {
    setDeleting(true);
    try {
      await deleteAccount();
      toast.success('Account deleted successfully. Redirecting to login...');
      
      // Wait 2 seconds then redirect to login
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (error) {
      toast.error('Failed to delete account. Please try again.');
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  }

  function handleRestorePurchases() {
    // This would integrate with Apple/Google IAP
    // For now, show a message
    toast.info('Checking for purchases...', { duration: 2000 });
    
    setTimeout(() => {
      // Simulate restore - in production, this would call IAP APIs
      toast.success('No previous purchases found to restore');
    }, 2000);
  }

  async function handleSavePreferences() {
    setSaving(true);
    try {
      await updateNotificationPreferences({
        weekly_summary_enabled: prefs.weekly_summary_enabled,
        weekly_summary_day: prefs.weekly_summary_day,
        weekly_summary_time: prefs.weekly_summary_time,
        categorization_reminders: prefs.categorization_reminders,
        categorization_reminder_threshold: prefs.categorization_reminder_threshold,
        tax_deadline_reminders: prefs.tax_deadline_reminders,
        tax_deadline_days_before: prefs.tax_deadline_days_before,
        receipt_match_alerts: prefs.receipt_match_alerts,
        large_expense_alerts: prefs.large_expense_alerts,
        large_expense_threshold: prefs.large_expense_threshold,
        email_notifications: prefs.email_notifications,
        email_address: prefs.email_address || null,
        push_notifications: prefs.push_notifications
      });
      toast.success('Preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  }

  function updatePref(key, value) {
    setPrefs(function(prev) {
      return { ...prev, [key]: value };
    });
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          <div className="skeleton h-40 rounded-lg" />
          <div className="skeleton h-80 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage backups and notification preferences</p>
      </div>

      <Tabs defaultValue="data-merge" className="space-y-6">
        <TabsList className="bg-muted flex-wrap">
          <TabsTrigger value="data-merge" data-testid="tab-data-merge">
            <GitMerge className="w-4 h-4 mr-2" /> Data & Merge
          </TabsTrigger>
          <TabsTrigger value="backup" data-testid="tab-backup">
            <Database className="w-4 h-4 mr-2" /> Backup
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">
            <Bell className="w-4 h-4 mr-2" /> Notifications
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">
            <Mail className="w-4 h-4 mr-2" /> Email
          </TabsTrigger>
          <TabsTrigger value="deadlines" data-testid="tab-deadlines">
            <Calendar className="w-4 h-4 mr-2" /> Tax Deadlines
          </TabsTrigger>
          <TabsTrigger value="autosync" data-testid="tab-autosync">
            <RefreshCw className="w-4 h-4 mr-2" /> Auto-Sync
          </TabsTrigger>
        </TabsList>
        
        {/* Data & Merge Tab */}
        <TabsContent value="data-merge">
          <div className="space-y-6">
            {/* Data Access Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-emerald-400" />
                  Data Access
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg border border-emerald-400/30 bg-emerald-400/5">
                    <div className="flex items-center gap-3 mb-2">
                      <Briefcase className="w-5 h-5 text-emerald-400" />
                      <span className="font-semibold">Business Data</span>
                      {dataAccess.has_business_access && (
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Active</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Track business expenses, income, mileage, and tax deductions.
                    </p>
                  </div>
                  
                  <div className="p-4 rounded-lg border border-blue-400/30 bg-blue-400/5">
                    <div className="flex items-center gap-3 mb-2">
                      <Home className="w-5 h-5 text-blue-400" />
                      <span className="font-semibold">Personal Data</span>
                      {dataAccess.has_personal_access && (
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Active</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Track personal expenses, budgets, and financial goals.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Merge Data Option */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GitMerge className="w-5 h-5 text-[hsl(var(--accent))]" />
                  Merge Data
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div className="space-y-1">
                    <Label className="text-base font-medium">Enable Data Merge</Label>
                    <p className="text-sm text-muted-foreground">
                      View combined business and personal data in unified reports and dashboards.
                    </p>
                  </div>
                  <Switch
                    checked={dataAccess.merge_enabled}
                    onCheckedChange={handleToggleMerge}
                    disabled={togglingMerge || !dataAccess.can_merge_data}
                    data-testid="toggle-merge-data"
                  />
                </div>
                
                {dataAccess.merge_enabled && (
                  <div className="p-4 rounded-lg border border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/5">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-5 h-5 text-[hsl(var(--accent))]" />
                      <span className="font-medium text-[hsl(var(--accent))]">Merge Enabled</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Your business and personal data will be combined in reports. Individual data remains separate and can be accessed via the sidebar sections.
                    </p>
                  </div>
                )}
                
                {!dataAccess.can_merge_data && (
                  <div className="p-4 rounded-lg border border-yellow-500/30 bg-yellow-500/5">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-500" />
                      <span className="font-medium text-yellow-500">Upgrade Required</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Data merge requires access to both Business and Personal data. Please upgrade your subscription.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Data Backup Tab */}
        <TabsContent value="backup">
          <div className="space-y-6">
            {/* Backup Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-[hsl(var(--accent))]" />
                  Your Data Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                {backupSummary ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div className="p-4 rounded-lg bg-muted/50 text-center">
                      <p className="text-2xl font-bold">{backupSummary.totals?.transactions || 0}</p>
                      <p className="text-sm text-muted-foreground">Transactions</p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50 text-center">
                      <p className="text-2xl font-bold">{backupSummary.totals?.receipts || 0}</p>
                      <p className="text-sm text-muted-foreground">Receipts</p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50 text-center">
                      <p className="text-2xl font-bold">{backupSummary.totals?.mileage_entries || 0}</p>
                      <p className="text-sm text-muted-foreground">Mileage Entries</p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50 text-center">
                      <p className="text-2xl font-bold">{backupSummary.totals?.writeoffs || 0}</p>
                      <p className="text-sm text-muted-foreground">Write-offs</p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50 text-center">
                      <p className="text-2xl font-bold">{backupSummary.totals?.documents || 0}</p>
                      <p className="text-sm text-muted-foreground">Documents</p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50 text-center">
                      <p className="text-2xl font-bold">{backupSummary.totals?.folders || 0}</p>
                      <p className="text-sm text-muted-foreground">Folders</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground">Loading summary...</p>
                )}
              </CardContent>
            </Card>

            {/* Export Options */}
            <Card>
              <CardHeader>
                <CardTitle>Export Your Data</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-muted-foreground">
                  Download a complete backup of all your Zero Deficit Sync data. You can use this for personal records 
                  or to import into other accounting software.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="card-hover">
                    <CardContent className="p-6 text-center">
                      <FileJson className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--accent))]" />
                      <h3 className="font-semibold mb-2">JSON Export</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        All data in a single JSON file. Best for developers or importing to other apps.
                      </p>
                      <Button
                        variant="outline"
                        onClick={handleExportJson}
                        disabled={exporting}
                        data-testid="export-json-button"
                      >
                        {exporting && exportType === 'json' ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Export JSON
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="card-hover">
                    <CardContent className="p-6 text-center">
                      <FileArchive className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--warning))]" />
                      <h3 className="font-semibold mb-2">ZIP Export (with Files)</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Complete backup including all receipts and documents as actual files.
                      </p>
                      <Button
                        className="btn-primary"
                        onClick={handleExportZip}
                        disabled={exporting}
                        data-testid="export-zip-button"
                      >
                        {exporting && exportType === 'zip' ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Export ZIP
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                <Card className="bg-muted/30">
                  <CardContent className="p-4">
                    <p className="text-sm">
                      <strong>Note:</strong> Your data is always stored securely in the cloud. 
                      These exports are for your personal backup and records.
                    </p>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>

            {/* Help & Onboarding */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-[hsl(var(--accent))]" />
                  Help & Getting Started
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Product Tour</p>
                    <p className="text-sm text-muted-foreground">
                      Take a guided tour of Zero Deficit Sync's key features
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      if (user) {
                        const completedKey = `onboarding_completed_${user.email || user.id}`;
                        localStorage.removeItem(completedKey);
                      }
                      if (startTour) {
                        startTour();
                      }
                    }}
                  >
                    <HelpCircle className="w-4 h-4 mr-2" />
                    Restart Tour
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              {prefs ? (
                <div className="space-y-8">
                  {/* Push Notifications */}
                  <div className="space-y-4 pb-6 border-b border-border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${pushSubscribed ? 'bg-[rgba(34,197,94,0.1)]' : 'bg-muted'}`}>
                          {pushSubscribed ? (
                            <BellRing className="w-5 h-5 text-green-500" />
                          ) : (
                            <BellOff className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold">Push Notifications</h3>
                          <p className="text-sm text-muted-foreground">
                            {pushSupported 
                              ? 'Receive instant notifications in your browser'
                              : 'Push notifications are not supported in this browser'}
                          </p>
                        </div>
                      </div>
                      {pushSupported && (
                        <div className="flex items-center gap-2">
                          {pushSubscribed && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleSendTestPush}
                              disabled={sendingTest || pushLoading}
                              data-testid="test-push-button"
                            >
                              {sendingTest ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                              <span className="ml-2">Test</span>
                            </Button>
                          )}
                          <Switch
                            checked={pushSubscribed}
                            onCheckedChange={handleTogglePush}
                            disabled={pushLoading || pushPermission === 'denied'}
                            data-testid="push-toggle"
                          />
                        </div>
                      )}
                    </div>
                    {pushPermission === 'denied' && (
                      <div className="ml-12 p-3 rounded-lg bg-[rgba(239,68,68,0.1)] border border-red-500/20">
                        <p className="text-sm text-red-400">
                          Push notifications are blocked. Please enable them in your browser settings.
                        </p>
                      </div>
                    )}
                    {pushSubscribed && (
                      <div className="ml-12 p-3 rounded-lg bg-[rgba(34,197,94,0.1)] border border-green-500/20">
                        <p className="text-sm text-green-400 flex items-center gap-2">
                          <Smartphone className="w-4 h-4" />
                          Push notifications are active on this device
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Weekly Summary */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-[rgba(20,184,166,0.1)]">
                          <Clock className="w-5 h-5 text-[hsl(var(--accent))]" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Weekly Summary</h3>
                          <p className="text-sm text-muted-foreground">
                            Receive a weekly summary of your expenses and categorization status
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={prefs.weekly_summary_enabled}
                        onCheckedChange={function(v) { updatePref('weekly_summary_enabled', v); }}
                        data-testid="weekly-summary-toggle"
                      />
                    </div>
                    {prefs.weekly_summary_enabled && (
                      <div className="ml-12 grid grid-cols-2 gap-4">
                        <div>
                          <Label>Day of Week</Label>
                          <Select 
                            value={prefs.weekly_summary_day} 
                            onValueChange={function(v) { updatePref('weekly_summary_day', v); }}
                          >
                            <SelectTrigger data-testid="weekly-day-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {daysOfWeek.map(function(d) {
                                return <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>;
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Time</Label>
                          <Select 
                            value={prefs.weekly_summary_time} 
                            onValueChange={function(v) { updatePref('weekly_summary_time', v); }}
                          >
                            <SelectTrigger data-testid="weekly-time-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {timeOptions.map(function(t) {
                                return <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>;
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Categorization Reminders */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-[rgba(249,115,22,0.1)]">
                          <Receipt className="w-5 h-5 text-orange-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Categorization Reminders</h3>
                          <p className="text-sm text-muted-foreground">
                            Remind me when I have uncategorized transactions
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={prefs.categorization_reminders}
                        onCheckedChange={function(v) { updatePref('categorization_reminders', v); }}
                        data-testid="categorization-toggle"
                      />
                    </div>
                    {prefs.categorization_reminders && (
                      <div className="ml-12">
                        <Label>Remind when I have more than</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            type="number"
                            min="1"
                            max="100"
                            value={prefs.categorization_reminder_threshold}
                            onChange={function(e) { updatePref('categorization_reminder_threshold', parseInt(e.target.value) || 5); }}
                            className="w-24"
                            data-testid="categorization-threshold"
                          />
                          <span className="text-muted-foreground">uncategorized transactions</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Tax Deadline Reminders */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-[rgba(239,68,68,0.1)]">
                          <Calendar className="w-5 h-5 text-red-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Tax Deadline Reminders</h3>
                          <p className="text-sm text-muted-foreground">
                            Remind me before quarterly and annual tax deadlines
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={prefs.tax_deadline_reminders}
                        onCheckedChange={function(v) { updatePref('tax_deadline_reminders', v); }}
                        data-testid="deadline-toggle"
                      />
                    </div>
                    {prefs.tax_deadline_reminders && (
                      <div className="ml-12">
                        <Label>Remind me</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            type="number"
                            min="1"
                            max="30"
                            value={prefs.tax_deadline_days_before}
                            onChange={function(e) { updatePref('tax_deadline_days_before', parseInt(e.target.value) || 7); }}
                            className="w-24"
                            data-testid="deadline-days"
                          />
                          <span className="text-muted-foreground">days before deadline</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Receipt Match Alerts */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-[rgba(34,197,94,0.1)]">
                        <Receipt className="w-5 h-5 text-green-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Receipt Match Alerts</h3>
                        <p className="text-sm text-muted-foreground">
                          Notify me when a receipt is matched to a transaction
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={prefs.receipt_match_alerts}
                      onCheckedChange={function(v) { updatePref('receipt_match_alerts', v); }}
                      data-testid="receipt-match-toggle"
                    />
                  </div>

                  {/* Large Expense Alerts */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-[rgba(139,92,246,0.1)]">
                          <DollarSign className="w-5 h-5 text-purple-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Large Expense Alerts</h3>
                          <p className="text-sm text-muted-foreground">
                            Notify me of unusually large expenses
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={prefs.large_expense_alerts}
                        onCheckedChange={function(v) { updatePref('large_expense_alerts', v); }}
                        data-testid="large-expense-toggle"
                      />
                    </div>
                    {prefs.large_expense_alerts && (
                      <div className="ml-12">
                        <Label>Alert for expenses over</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-muted-foreground">$</span>
                          <Input
                            type="number"
                            min="50"
                            max="10000"
                            step="50"
                            value={prefs.large_expense_threshold}
                            onChange={function(e) { updatePref('large_expense_threshold', parseFloat(e.target.value) || 500); }}
                            className="w-32"
                            data-testid="large-expense-threshold"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Email Notifications */}
                  <div className="space-y-4 pt-4 border-t border-border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-[rgba(59,130,246,0.1)]">
                          <Mail className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Email Notifications</h3>
                          <p className="text-sm text-muted-foreground">
                            Receive notifications via email
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={prefs.email_notifications}
                        onCheckedChange={function(v) { updatePref('email_notifications', v); }}
                        data-testid="email-toggle"
                      />
                    </div>
                    {prefs.email_notifications && (
                      <div className="ml-12">
                        <Label>Email Address (optional override)</Label>
                        <Input
                          type="email"
                          placeholder="Use account email"
                          value={prefs.email_address || ''}
                          onChange={function(e) { updatePref('email_address', e.target.value); }}
                          className="mt-1"
                          data-testid="email-address-input"
                        />
                      </div>
                    )}
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end pt-4">
                    <Button 
                      className="btn-primary" 
                      onClick={handleSavePreferences} 
                      disabled={saving}
                      data-testid="save-preferences-button"
                    >
                      {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      <Save className="w-4 h-4 mr-2" /> Save Preferences
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">Loading preferences...</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Preferences Tab */}
        <TabsContent value="email">
          <div className="space-y-6">
            {/* Digital Shoebox */}
            <Card className="border-[hsl(var(--accent))]/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[hsl(var(--accent))]" />
                  Digital Shoebox
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Forward receipts to this email address to automatically add them to your account
                </p>
              </CardHeader>
              <CardContent>
                <div className="p-4 rounded-lg bg-muted/50 border border-border">
                  <p className="text-xs text-muted-foreground mb-2">Your unique receipt email:</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 rounded bg-background border text-sm font-mono text-[hsl(var(--accent))]">
                      {shoeboxEmail || 'Loading...'}
                    </code>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        if (shoeboxEmail) {
                          navigator.clipboard.writeText(shoeboxEmail);
                          toast.success('Email copied to clipboard');
                        }
                      }}
                      disabled={!shoeboxEmail}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email Notification Preferences */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-[hsl(var(--accent))]" />
                  Email Notifications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-border">
                  <div>
                    <Label className="text-base">Weekly Mileage Summary</Label>
                    <p className="text-sm text-muted-foreground">Receive a weekly digest of your trips and deductions</p>
                  </div>
                  <Switch 
                    checked={emailPrefs.weekly_summary} 
                    onCheckedChange={(checked) => setEmailPrefs(prev => ({...prev, weekly_summary: checked}))}
                    data-testid="email-weekly-summary" 
                  />
                </div>
                <div className="flex items-center justify-between py-3 border-b border-border">
                  <div>
                    <Label className="text-base">Tax Deadline Reminders</Label>
                    <p className="text-sm text-muted-foreground">Get notified before important tax deadlines</p>
                  </div>
                  <Switch 
                    checked={emailPrefs.tax_reminders}
                    onCheckedChange={(checked) => setEmailPrefs(prev => ({...prev, tax_reminders: checked}))}
                    data-testid="email-tax-reminders" 
                  />
                </div>
                <div className="flex items-center justify-between py-3 border-b border-border">
                  <div>
                    <Label className="text-base">Receipt Notifications</Label>
                    <p className="text-sm text-muted-foreground">Confirmation when receipts are processed</p>
                  </div>
                  <Switch 
                    checked={emailPrefs.receipt_notifications}
                    onCheckedChange={(checked) => setEmailPrefs(prev => ({...prev, receipt_notifications: checked}))}
                    data-testid="email-receipt-notifications" 
                  />
                </div>
                <div className="flex items-center justify-between py-3">
                  <div>
                    <Label className="text-base">Subscription Updates</Label>
                    <p className="text-sm text-muted-foreground">Billing confirmations and plan changes</p>
                  </div>
                  <Switch 
                    checked={emailPrefs.subscription_updates}
                    onCheckedChange={(checked) => setEmailPrefs(prev => ({...prev, subscription_updates: checked}))}
                    data-testid="email-subscription-updates" 
                  />
                </div>
                <div className="pt-4">
                  <Button 
                    className="btn-primary"
                    onClick={async () => {
                      try {
                        await updateEmailPreferences(emailPrefs);
                        toast.success('Email preferences saved');
                      } catch (error) {
                        toast.error('Failed to save preferences');
                      }
                    }}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Email Preferences
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tax Deadlines Tab */}
        <TabsContent value="deadlines">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[hsl(var(--accent))]" />
                Upcoming Tax Deadlines
              </CardTitle>
            </CardHeader>
            <CardContent>
              {deadlines.length > 0 ? (
                <div className="space-y-4">
                  {deadlines.map(function(deadline, index) {
                    return (
                      <div 
                        key={index} 
                        className={`p-4 rounded-lg border ${deadline.is_urgent ? 'border-red-500/50 bg-red-500/5' : 'border-border'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {deadline.is_urgent ? (
                              <div className="p-2 rounded-lg bg-red-500/10">
                                <AlertTriangle className="w-5 h-5 text-red-500" />
                              </div>
                            ) : (
                              <div className="p-2 rounded-lg bg-muted">
                                <Calendar className="w-5 h-5 text-muted-foreground" />
                              </div>
                            )}
                            <div>
                              <h3 className="font-semibold">{deadline.name}</h3>
                              <p className="text-sm text-muted-foreground">{deadline.description}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{deadline.date}</p>
                            <Badge variant={deadline.is_urgent ? 'destructive' : 'outline'}>
                              {deadline.days_until === 0 ? 'Today' : `${deadline.days_until} days`}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No upcoming deadlines in the next few months.
                </p>
              )}

              <Card className="bg-muted/30 mt-6">
                <CardContent className="p-4">
                  <p className="text-sm">
                    <strong>Tip:</strong> Enable tax deadline reminders in the Notifications tab 
                    to get alerts before these due dates.
                  </p>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Auto-Sync Tab */}
        <TabsContent value="autosync">
          <div className="space-y-6">
            {/* Plaid Connection Status */}
            {!plaidStatus.connected ? (
              <Card>
                <CardContent className="p-6">
                  <div className="text-center space-y-4">
                    <div className="inline-flex p-4 rounded-full bg-muted">
                      <RefreshCw className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">Connect Your Bank</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Connect your bank account to enable automatic transaction syncing
                      </p>
                    </div>
                    <Button onClick={() => window.location.href = '/transactions'} className="btn-primary">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Go to Transactions to Connect
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Auto-Sync Settings */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <RefreshCw className="w-5 h-5 text-[hsl(var(--accent))]" />
                      Automatic Sync
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label htmlFor="auto-sync-toggle">Enable Auto-Sync</Label>
                        <p className="text-sm text-muted-foreground">
                          Automatically import new transactions from your bank
                        </p>
                      </div>
                      <Switch
                        id="auto-sync-toggle"
                        checked={prefs?.auto_sync_enabled || false}
                        onCheckedChange={async (checked) => {
                          try {
                            await updateNotificationPreferences({ auto_sync_enabled: checked });
                            setPrefs({ ...prefs, auto_sync_enabled: checked });
                            toast.success(`Auto-sync ${checked ? 'enabled' : 'disabled'}`);
                          } catch (error) {
                            toast.error('Failed to update settings');
                          }
                        }}
                        data-testid="auto-sync-toggle"
                      />
                    </div>

                    {prefs?.auto_sync_enabled && (
                      <div className="space-y-2">
                        <Label>Sync Frequency</Label>
                        <Select
                          value={String(prefs?.auto_sync_frequency || 3600)}
                          onValueChange={async (value) => {
                            try {
                              await updateNotificationPreferences({ auto_sync_frequency: Number(value) });
                              setPrefs({ ...prefs, auto_sync_frequency: Number(value) });
                              toast.success('Sync frequency updated');
                            } catch (error) {
                              toast.error('Failed to update frequency');
                            }
                          }}
                        >
                          <SelectTrigger data-testid="sync-frequency-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="900">Every 15 minutes</SelectItem>
                            <SelectItem value="1800">Every 30 minutes</SelectItem>
                            <SelectItem value="3600">Every hour</SelectItem>
                            <SelectItem value="10800">Every 3 hours</SelectItem>
                            <SelectItem value="21600">Every 6 hours</SelectItem>
                            <SelectItem value="43200">Every 12 hours</SelectItem>
                            <SelectItem value="86400">Once a day</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          New transactions will be automatically imported at this interval
                        </p>
                      </div>
                    )}

                    <div className="pt-4 border-t">
                      <Button
                        onClick={async () => {
                          setSyncing(true);
                          try {
                            const res = await triggerAutoSync();
                            if (res.data.imported > 0) {
                              toast.success(`Synced ${res.data.imported} new transactions!`);
                            } else {
                              toast.info('No new transactions to import');
                            }
                            await fetchData();
                          } catch (error) {
                            toast.error('Sync failed. Please try again.');
                          } finally {
                            setSyncing(false);
                          }
                        }}
                        disabled={syncing}
                        className="w-full"
                        variant="outline"
                        data-testid="manual-sync-button"
                      >
                        {syncing ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        {syncing ? 'Syncing...' : 'Sync Now'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Sync History */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <History className="w-5 h-5 text-[hsl(var(--accent))]" />
                      Sync History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {syncHistory && syncHistory.length > 0 ? (
                      <div className="space-y-3">
                        {syncHistory.map((sync, index) => (
                          <div
                            key={sync.id || index}
                            className="flex items-center justify-between p-4 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              {sync.success ? (
                                <CheckCircle2 className="w-5 h-5 text-[hsl(var(--success))]" />
                              ) : (
                                <XCircle className="w-5 h-5 text-[hsl(var(--destructive))]" />
                              )}
                              <div>
                                <p className="font-medium text-sm">
                                  {sync.imported_count > 0 
                                    ? `${sync.imported_count} transaction${sync.imported_count > 1 ? 's' : ''} imported` 
                                    : 'No new transactions'}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(sync.timestamp).toLocaleString()}
                                </p>
                              </div>
                            </div>
                            <Badge variant={sync.success ? 'outline' : 'destructive'}>
                              {sync.items_synced} bank{sync.items_synced > 1 ? 's' : ''}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <History className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                        <p className="text-muted-foreground">No sync history yet</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Your automatic syncs will appear here
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Account Management Section */}
      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-500" />
            Account Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Restore Purchases */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-border">
            <div>
              <h3 className="font-semibold">Restore Purchases</h3>
              <p className="text-sm text-muted-foreground">
                Restore any previous subscription purchases
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleRestorePurchases}
              data-testid="restore-purchases-button"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Restore
            </Button>
          </div>

          {/* Delete Account */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-red-500/30 bg-red-500/5">
            <div>
              <h3 className="font-semibold text-red-600 dark:text-red-400">Delete Account</h3>
              <p className="text-sm text-muted-foreground">
                Permanently delete your account and all associated data
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={() => setDeleteDialogOpen(true)}
              data-testid="delete-account-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Account
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Delete Account?
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p className="font-semibold">This will permanently delete:</p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Your account and profile</li>
                <li>All transactions and receipts</li>
                <li>All documents and mileage logs</li>
                <li>Bank connections (Plaid)</li>
                <li>All settings and preferences</li>
              </ul>
              <p className="font-semibold text-red-600 dark:text-red-400">
                This action cannot be undone!
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteAccount}
              disabled={deleting}
              data-testid="confirm-delete-button"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Yes, Delete Everything
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
